import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Box,
  Card,
  CardContent,
  CardHeader,
  Table,
  Typography,
} from "@mui/material";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableRow from "@mui/material/TableRow";
import { PiReadCvLogo } from "react-icons/pi";

import Button from "../../Button/Button";
import styles from "../../Button/Button.module.css";
import {
  pathToCleaningLog,
  pathToCleaningSchedule,
  pathToMapDetail,
} from "../../../helpers/paths";
import LogTable from "../CleaningLog/LogTable";
import Layout from "../../templates/Layout";
import { MapViewer } from "../../Form/MapViewer";
import { getPreviousMap, getCurrentMap } from "../../../helpers/scheduleHelper";
import { InfoDialog } from "../../Form/InfoFialog";
import { useSafeQueryId } from "hooks/useSafeId";
import { useListRobotQuery } from "hooks/worker/useRobot";
import { useListCleaningEventByRobotQuery } from "hooks/worker/useCleaningEvent";
import { useListMapQuery } from "hooks/worker/useMap";
import { CleaningMapItem } from "types/response/map";
import RobotDetailHeaderMenu from "./RobotDetailHeaderMenu";
import { DeviceStatusesView } from "./DeviceStatusView";
import { ConsumablePartsView } from "./ConsumablePartsView";
import { parseExternalServiceCodeType } from "types/externalService";

export const RobotDetailPage = () => {
  const robotId = useSafeQueryId("id");
  const { data: robots } = useListRobotQuery();
  const robot = robots?.find((r) => r.robotId === robotId);
  const { data: logs = [] } = useListCleaningEventByRobotQuery({ robotId });
  const { data: maps = [] } = useListMapQuery();
  const navigate = useNavigate();

  const parsedExCode = parseExternalServiceCodeType(robot?.exSrvCode ?? "");

  const [nextMap, setNextMap] = useState<CleaningMapItem>();
  const [prevMap, setPrevMap] = useState<CleaningMapItem>();

  // ロボット情報編集ダイアログ向け
  const [logMap, setLogMap] = useState<CleaningMapItem>();

  // ロボット設定保存時フィードバック用
  const [infoDialogOpen, setInfoDialogOpen] = useState(false);

  const onClickTimerSetting = () => {
    if (!robot) {
      return;
    }
    navigate(pathToCleaningSchedule(robot.robotId));
  };

  const onClickLog = () => {
    if (!robot) {
      return;
    }
    navigate(pathToCleaningLog(robot.robotId));
  };

  useEffect(() => {
    if (!robot) return;

    // 翌日以降を順に探す
    const currentDate = new Date();
    const nextMap = getCurrentMap(robot.schedules, maps, currentDate);
    setNextMap(nextMap);

    const prevMap = getPreviousMap(robot.schedules, maps, currentDate);
    setPrevMap(prevMap);
  }, [maps, robot]);

  useEffect(() => {
    if (logs.length === 0 || maps.length === 0) {
      return;
    }

    setLogMap(maps.find((m) => m.mapId === logs[0].mapId));
  }, [logs, maps]);

  useEffect(() => {
    if (robot && !parsedExCode) {
      throw new Error(`不正なコードを取得しました: ${robot.exSrvCode}`);
    }
  }, [parsedExCode, robot]);

  return (
    <Layout title={`ロボット詳細情報 ${robot?.robotName || ""}`}>
      {robot && parsedExCode && (
        <Card sx={{ minWidth: 250, overflowWrap: "anywhere" }}>
          <Box className={styles.buttonWrapper2}>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                width: "100%",
              }}
            >
              <CardHeader
                title={robot.robotName}
                titleTypographyProps={{ variant: "h5" }}
              />
              <RobotDetailHeaderMenu robot={robot} />
            </Box>
          </Box>

          <CardContent sx={{ p: 2 }}>
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                p: 2,
              }}
            >
              {logMap && logs.length > 0 && logs[0].imagePath && (
                <MapViewer
                  src={logs[0].imagePath}
                  displaySetting={logMap.displaySetting}
                />
              )}
            </Box>
            <Box sx={{ mt: 2 }}>
              <TableContainer component={Card}>
                <Table aria-label="simple table">
                  <TableBody>
                    <TableRow>
                      <TableCell>ID</TableCell>
                      <TableCell>{robot.exRobotId}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell>名前</TableCell>
                      <TableCell>{robot.robotName}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell>説明</TableCell>
                      <TableCell sx={{ whiteSpace: "pre-line" }}>
                        {robot.description}
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell>設定マップ</TableCell>
                      <TableCell>
                        {/* {robot.cleaningMap && ( */}
                        {prevMap && (
                          <Typography
                            component={Link}
                            to={pathToMapDetail(prevMap.mapId)}
                            variant="inherit"
                          >
                            {/* {robot.cleaningMap.name} */}
                            {prevMap.mapName}
                          </Typography>
                        )}
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell sx={{ whiteSpace: "nowrap" }}>
                        次回設定マップ
                      </TableCell>
                      <TableCell>
                        {nextMap && (
                          <Typography
                            component={Link}
                            color="secondary"
                            to={pathToMapDetail(nextMap.mapId)}
                            variant="inherit"
                          >
                            {nextMap?.mapName}
                          </Typography>
                        )}
                      </TableCell>
                    </TableRow>
                    <DeviceStatusesView
                      deviceStatuses={robot.deviceStatuses}
                      exCode={parsedExCode}
                    />
                    <ConsumablePartsView
                      consumableParts={robot.consumableParts}
                      exSrvCode={parsedExCode}
                      robotName={robot.robotName}
                      robotId={robot.robotId}
                    />
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>

            <Box sx={{ display: "flex", mt: 4, mb: 1, alignItems: "center" }}>
              <Typography variant="h6">直近の清掃ログ</Typography>
              <Button
                sx={{ ml: "auto", p: 1 }}
                variant="outlined"
                onClick={onClickLog}
                startIcon={<PiReadCvLogo />}
              >
                清掃ログ
              </Button>
            </Box>
            <LogTable logs={logs} maps={maps} enableMapName displayLimit={5} />

            <Typography variant="h6" sx={{ mt: 2 }}>
              設定
            </Typography>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                gap: 1,
              }}
            >
              <Button sx={{ flex: 1 }} onClick={onClickTimerSetting}>
                スケジュール<br></br>設定
              </Button>
            </Box>
          </CardContent>
        </Card>
      )}
      <InfoDialog
        open={infoDialogOpen}
        onClickOk={() => setInfoDialogOpen(false)}
        title="設定を更新しました。"
      />
    </Layout>
  );
};
